import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowDownLeft, ArrowUpRight, Smartphone, Search, Filter } from 'lucide-react';
import { mobilePaymentsApi, clientsApi } from '../lib/api';
import { toast } from 'sonner';

const fmt = (v) => v != null ? `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : '-';

const MobilePaymentsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showReceive, setShowReceive] = useState(false);
  const [showDisburse, setShowDisburse] = useState(false);
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: '', amount: '', phone_number: '', purpose: 'loan_repayment', loan_id: '', account_id: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); loadClients(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await mobilePaymentsApi.list({ tx_type: filter.type || undefined, status: filter.status || undefined });
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadClients = async () => {
    try {
      const res = await clientsApi.list();
      setClients(res.data.clients || res.data || []);
    } catch (e) { console.error(e); }
  };

  const resetForm = () => setForm({ client_id: '', amount: '', phone_number: '', purpose: 'loan_repayment', loan_id: '', account_id: '' });

  const handleReceive = async () => {
    setSubmitting(true);
    try {
      const res = await mobilePaymentsApi.receive({ ...form, amount: parseFloat(form.amount) });
      toast.success(`Payment received: ${res.data.tx_ref}`);
      setShowReceive(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  const handleDisburse = async () => {
    setSubmitting(true);
    try {
      const res = await mobilePaymentsApi.disburse({ ...form, amount: parseFloat(form.amount) });
      toast.success(`Disbursement queued: ${res.data.tx_ref}`);
      setShowDisburse(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  const selectedClient = clients.find(c => c.id === form.client_id);

  return (
    <div className="space-y-6 fade-in" data-testid="mobile-payments-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Operations</span><span>/</span><span className="text-[#1f1f1f] font-medium">Mobile Payments</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">TRANSACTION MANAGEMENT</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Mobile Payments</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { resetForm(); setShowReceive(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="receive-payment-btn">
            <ArrowDownLeft className="w-4 h-4 mr-2" />Receive Payment
          </Button>
          <Button onClick={() => { resetForm(); setShowDisburse(true); }} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="disburse-btn">
            <ArrowUpRight className="w-4 h-4 mr-2" />Disburse
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-end">
        <div>
          <Label className="text-xs text-gray-500">Type</Label>
          <select className="h-9 border border-gray-200 rounded px-3 text-sm" value={filter.type} onChange={e => setFilter({...filter, type: e.target.value})} data-testid="filter-type">
            <option value="">All Types</option>
            <option value="receive">Received</option>
            <option value="disburse">Disbursed</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Status</Label>
          <select className="h-9 border border-gray-200 rounded px-3 text-sm" value={filter.status} onChange={e => setFilter({...filter, status: e.target.value})} data-testid="filter-status">
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending_gateway">Pending Gateway</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <Button variant="outline" onClick={load} data-testid="apply-filter-btn"><Filter className="w-4 h-4 mr-1" />Filter</Button>
      </div>

      {/* Gateway Notice */}
      <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
        <Smartphone className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Mobile Payment Gateway Integration Pending</p>
          <p className="mt-0.5">Transactions are recorded in the system. Actual mobile money transfers will be processed once the payment gateway API is connected. Disbursements are queued for processing.</p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="transactions-table">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Ref</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Phone</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Purpose</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : transactions.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-gray-400">No mobile transactions recorded yet</td></tr>
              ) : (
                transactions.map(tx => (
                  <tr key={tx.id} className="border-b hover:bg-gray-50" data-testid={`tx-row-${tx.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-[#1f1f1f]">{tx.tx_ref}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${tx.type === 'receive' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                        {tx.type === 'receive' ? 'Inbound' : 'Outbound'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{tx.client_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">{tx.phone_number}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-medium">{fmt(tx.amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{tx.purpose?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        tx.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                        tx.status === 'pending_gateway' ? 'bg-amber-50 text-amber-700' :
                        'bg-red-50 text-red-700'
                      }`}>{tx.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{tx.created_at ? new Date(tx.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500">{total} transaction(s)</div>
      </div>

      {/* Receive Payment Dialog */}
      <Dialog open={showReceive} onOpenChange={setShowReceive}>
        <DialogContent aria-describedby="receive-desc">
          <DialogHeader><DialogTitle>Receive Mobile Payment</DialogTitle></DialogHeader>
          <p id="receive-desc" className="sr-only">Record an inbound mobile payment from a client</p>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <select className="w-full h-9 border border-gray-200 rounded px-3 text-sm" value={form.client_id} onChange={e => {
                const c = clients.find(cl => cl.id === e.target.value);
                setForm({...form, client_id: e.target.value, phone_number: c?.phone || '' });
              }} data-testid="receive-client">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} - {c.client_number}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="receive-amount" /></div>
              <div><Label>Phone Number *</Label><Input value={form.phone_number} onChange={e => setForm({...form, phone_number: e.target.value})} data-testid="receive-phone" /></div>
            </div>
            <div>
              <Label>Purpose</Label>
              <select className="w-full h-9 border border-gray-200 rounded px-3 text-sm" value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} data-testid="receive-purpose">
                <option value="loan_repayment">Loan Repayment</option>
                <option value="savings_deposit">Savings Deposit</option>
                <option value="fee_payment">Fee Payment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceive(false)}>Cancel</Button>
            <Button onClick={handleReceive} disabled={submitting || !form.client_id || !form.amount} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-receive-btn">
              {submitting ? 'Processing...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disburse Dialog */}
      <Dialog open={showDisburse} onOpenChange={setShowDisburse}>
        <DialogContent aria-describedby="disburse-desc">
          <DialogHeader><DialogTitle>Disburse Mobile Payment</DialogTitle></DialogHeader>
          <p id="disburse-desc" className="sr-only">Initiate an outbound mobile payment to a client</p>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <select className="w-full h-9 border border-gray-200 rounded px-3 text-sm" value={form.client_id} onChange={e => {
                const c = clients.find(cl => cl.id === e.target.value);
                setForm({...form, client_id: e.target.value, phone_number: c?.phone || '' });
              }} data-testid="disburse-client">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} - {c.client_number}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="disburse-amount" /></div>
              <div><Label>Phone Number *</Label><Input value={form.phone_number} onChange={e => setForm({...form, phone_number: e.target.value})} data-testid="disburse-phone" /></div>
            </div>
            <div>
              <Label>Purpose *</Label>
              <select className="w-full h-9 border border-gray-200 rounded px-3 text-sm" value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} data-testid="disburse-purpose">
                <option value="loan_disbursement">Loan Disbursement</option>
                <option value="savings_withdrawal">Savings Withdrawal</option>
                <option value="refund">Refund</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisburse(false)}>Cancel</Button>
            <Button onClick={handleDisburse} disabled={submitting || !form.client_id || !form.amount || !form.purpose} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="confirm-disburse-btn">
              {submitting ? 'Processing...' : 'Queue Disbursement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobilePaymentsPage;
